import { useEffect, useState } from 'react';
import { Trash2, Mail, Phone, Calendar, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AdminDevis() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/devis');
      setItems(r.data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await api.delete(`/admin/devis/${id}`);
      setItems(it => it.filter(x => x.id !== id));
      toast.success('Demande supprimée');
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="h2 text-anthracite-950">Demandes de devis</h1>
        <p className="text-muted-foreground mt-1">{items.length} demande{items.length > 1 ? 's' : ''} reçue{items.length > 1 ? 's' : ''}</p>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-700" /></div>
      ) : items.length === 0 ? (
        <div className="card-base p-12 text-center text-muted-foreground">Aucune demande pour le moment.</div>
      ) : (
        <div className="card-base overflow-hidden" data-testid="admin-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-anthracite-100/50 text-left">
                <tr>
                  <th className="p-3 font-medium">Date</th>
                  <th className="p-3 font-medium">Demandeur</th>
                  <th className="p-3 font-medium">Contact</th>
                  <th className="p-3 font-medium">Type</th>
                  <th className="p-3 font-medium">Trajet</th>
                  <th className="p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.id} className="border-t hover:bg-anthracite-100/30">
                    <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString('fr-FR')}</td>
                    <td className="p-3 font-medium text-anthracite-900">{d.prenom} {d.nom}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-0.5">
                        <a href={`tel:${d.telephone}`} className="text-brand-700 text-xs">{d.telephone}</a>
                        <a href={`mailto:${d.email}`} className="text-muted-foreground text-xs">{d.email}</a>
                      </div>
                    </td>
                    <td className="p-3 text-xs">{d.type_transport}</td>
                    <td className="p-3 text-xs text-muted-foreground max-w-[240px] truncate">{d.adresse_depart} → {d.adresse_arrivee}</td>
                    <td className="p-3 whitespace-nowrap">
                      <Button variant="outline" size="sm" onClick={() => setSelected(d)}>Détails</Button>
                      <Button variant="outline" size="sm" onClick={() => remove(d.id)} className="ml-2 text-destructive border-destructive/30 hover:bg-destructive/10">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>Détails de la demande</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Info label="Demandeur" value={`${selected.prenom} ${selected.nom}`} />
                  <Info label="Téléphone" value={selected.telephone} />
                  <Info label="Email" value={selected.email} />
                  <Info label="Type de transport" value={selected.type_transport} />
                  <Info label="Patient" value={`${selected.patient_prenom || ''} ${selected.patient_nom || ''}`.trim() || '—'} />
                  <Info label="Mobilité" value={selected.mobilite || '—'} />
                  <Info label="Adresse départ" value={selected.adresse_depart} />
                  <Info label="Adresse arrivée" value={selected.adresse_arrivee} />
                  <Info label="Date" value={selected.date_trajet} />
                  <Info label="Heure" value={selected.heure_trajet} />
                  <Info label="Aller-retour" value={selected.aller_retour ? 'Oui' : 'Non'} />
                  <Info label="Oxygène" value={selected.oxygene ? 'Oui' : 'Non'} />
                  <Info label="Accompagnant" value={selected.accompagnant ? 'Oui' : 'Non'} />
                </div>
                {selected.remarques && (
                  <div>
                    <div className="text-xs text-muted-foreground">Remarques</div>
                    <div className="mt-1 p-3 rounded-lg bg-anthracite-100/50 text-anthracite-900 text-sm whitespace-pre-wrap">{selected.remarques}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium text-anthracite-900">{value || '—'}</div>
    </div>
  );
}
